import type { CSSProperties } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const WRAP: CSSProperties = {
  minHeight: "100dvh",
  background: "var(--d-ink)",
  color: "var(--d-fg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};
const CARD: CSSProperties = {
  background: "var(--d-ink-2)",
  border: "1px solid var(--d-line)",
  borderRadius: 22,
  padding: 24,
  maxWidth: 420,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  textAlign: "center",
};
const TITLE: CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: 24,
  color: "var(--d-fg)",
  margin: 0,
};
const INSTRUCTION: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 14,
  color: "var(--d-fg-muted)",
  margin: 0,
};
const LINK: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 48,
  marginTop: 8,
  borderRadius: 999,
  background: "var(--d-violet)",
  color: "#fff",
  textDecoration: "none",
  fontFamily: "var(--font-body)",
  fontSize: 14,
  fontWeight: 600,
};
const ERROR_BANNER: CSSProperties = {
  background: "var(--d-ember)",
  color: "#fff",
  borderRadius: 12,
  padding: "8px 12px",
  fontFamily: "var(--font-body)",
  fontSize: 13,
};

/** Router-state shape a redirect into `/login` may carry, e.g. from a
 * future "your session was revoked" flow. Nothing sets it yet — the banner
 * simply renders nothing when absent. */
interface LoginLocationState {
  error?: string;
}

/** DUE-221 (Task 12): the only entry point into the standalone browser
 * admin. There is no password — the operator gets in via the bot's
 * `/admin_web` command, which mints a 5-minute one-time code redeemed by
 * `AuthCallbackPage`. This screen just points them at the bot. */
export function LoginPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const location = useLocation();
  const state = (location.state ?? null) as LoginLocationState | null;
  const botUsername = import.meta.env.VITE_BOT_USERNAME;

  return (
    <div style={WRAP}>
      <div style={CARD}>
        {state?.error && <div style={ERROR_BANNER}>{state.error}</div>}
        <h1 style={TITLE}>{t("web_login.title")}</h1>
        <p style={INSTRUCTION}>{t("web_login.instruction")}</p>
        {botUsername && (
          <a
            href={`https://t.me/${botUsername}`}
            style={LINK}
            target="_blank"
            rel="noreferrer noopener"
          >
            {t("web_login.open_bot")}
          </a>
        )}
      </div>
    </div>
  );
}
